import { useState, useEffect } from 'react'
import { X, Plus, Loader2, ClipboardList } from 'lucide-react'
import api from '../../core/services/api'
import type { SchedaTemplateList } from '../../types/schede'

interface SchedaIstanzaModalProps {
  entityType: string
  entityId: string
  entityLabel?: string
  onClose: () => void
  onCreated: () => void
}

export default function SchedaIstanzaModal({
  entityType,
  entityId,
  entityLabel,
  onClose,
  onCreated,
}: SchedaIstanzaModalProps) {
  const [templates, setTemplates] = useState<SchedaTemplateList[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTemplates()
  }, [entityType])

  const loadTemplates = async () => {
    try {
      const res = await api.get(`/v1/schede-dinamiche/templates-per-tipo/${entityType}`)
      setTemplates(res.data)
    } catch {
      setError('Errore nel caricamento dei template')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (templateId: string) => {
    setCreating(templateId)
    setError(null)
    try {
      await api.post('/v1/schede-dinamiche/istanze', {
        template_id: templateId,
        entity_type: entityType,
        entity_id: entityId,
        entity_label: entityLabel || '',
      })
      onCreated()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nella creazione')
      setCreating(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="w-full max-w-md rounded-xl shadow-xl overflow-hidden border"
        style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <h3 className="text-base font-semibold flex items-center gap-2">
            <ClipboardList className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
            Nuova Scheda
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--color-text-tertiary)' }} />
              <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                Nessun template disponibile per questo tipo di entità
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
                Crea un template in Configurazioni {'>'} Schede Dinamiche
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                Seleziona un template per creare una nuova scheda:
              </p>
              {templates.map(tpl => (
                <button
                  key={tpl.id}
                  onClick={() => handleCreate(tpl.id)}
                  disabled={creating !== null}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors hover:opacity-90 disabled:opacity-50"
                  style={{
                    borderColor: 'var(--color-border)',
                    backgroundColor: creating === tpl.id ? 'var(--color-bg-surface)' : 'transparent',
                  }}
                >
                  {creating === tpl.id ? (
                    <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                  ) : (
                    <Plus className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{tpl.nome}</div>
                    {tpl.descrizione && (
                      <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-tertiary)' }}>
                        {tpl.descrizione}
                      </div>
                    )}
                    <div className="text-[10px] mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
                      {tpl.codice} · v{tpl.versione} · {tpl.fields_count} campi
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {error && (
            <p className="text-xs mt-3 text-center" style={{ color: 'var(--color-danger, #ef4444)' }}>
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex justify-end px-5 py-3 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            onClick={onClose}
            disabled={creating !== null}
            className="px-4 py-2 text-sm rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  )
}
